import { AfterViewInit, Directive, ElementRef, Input, OnChanges } from '@angular/core';

// Replaces ngx-swiper-wrapper's [swiper] directive (GX-7106) on a Swiper 12 <swiper-container init="false">.
// Like ngx-swiper-wrapper, `navigation: true` uses the host's own .swiper-button-next/.swiper-button-prev
// elements, and navigation/pagination selectors resolve inside the host. Those elements must sit in
// slot="container-end" so the web component renders them.

// The Swiper 12 parameters this app uses (swiper/types needs TypeScript 5).
export interface SwiperConfig {
  observer?: boolean;
  direction?: 'horizontal' | 'vertical';
  slidesPerView?: number;
  spaceBetween?: number;
  initialSlide?: number;
  keyboard?: boolean;
  grabCursor?: boolean;
  loop?: boolean;
  speed?: number;
  effect?: string;
  autoplay?: { delay: number, disableOnInteraction: boolean };
  breakpoints?: { [width: number]: { slidesPerView: number } };
  navigation?: boolean | { nextEl?: string, prevEl?: string };
  pagination?: boolean | { el?: string, clickable?: boolean };
}

@Directive({
    selector: 'swiper-container[appSwiper]',
    standalone: false
})
export class SwiperDirective implements AfterViewInit, OnChanges {
  @Input() appSwiper: SwiperConfig;
  private viewReady = false;
  private initialized = false;

  constructor(private elementRef: ElementRef) { }

  ngAfterViewInit() {
    this.viewReady = true;
    this.apply();
  }

  ngOnChanges() {
    this.apply();
  }

  // Components assign their config after view init; until then the binding is an empty object.
  private apply() {
    if (!this.viewReady || !this.appSwiper || !Object.keys(this.appSwiper).length) {
      return;
    }
    const host = this.elementRef.nativeElement;
    Object.assign(host, this.resolveElements(this.appSwiper));
    if (!this.initialized) {
      host.initialize();
      this.initialized = true;
    }
  }

  private resolveElements(config: SwiperConfig): { [param: string]: unknown } {
    const host: HTMLElement = this.elementRef.nativeElement;
    const { navigation, pagination, ...rest } = config;
    const params: { [param: string]: unknown } = { ...rest };
    if (navigation) {
      const selectors = navigation === true ? {} : navigation;
      params.navigation = {
        nextEl: host.querySelector<HTMLElement>(selectors.nextEl || '.swiper-button-next'),
        prevEl: host.querySelector<HTMLElement>(selectors.prevEl || '.swiper-button-prev'),
        // the app's buttons carry their own mat-icon arrows; Swiper 12 would inject a second one
        addIcons: false,
      };
    }
    if (pagination) {
      const options = pagination === true ? {} : pagination;
      params.pagination = { clickable: options.clickable, el: host.querySelector<HTMLElement>(options.el || '.swiper-pagination') };
    }
    return params;
  }
}
